import React, { useEffect } from 'react';
import { getConsent, initDefaultConsent } from '../lib/consent';

interface AnalyticsTrackerProps {
  googleAnalyticsId?: string;
  facebookPixelId?: string;
}

export const AnalyticsTracker: React.FC<AnalyticsTrackerProps> = ({
  googleAnalyticsId = 'G-BKDGNKX0Z1', // Real Google Analytics 4 ID
  facebookPixelId = '1234567890123456' // Replace with actual Facebook Pixel ID
}) => {
  useEffect(() => {
    // Google Analytics 4 Setup
    if (googleAnalyticsId && typeof window !== 'undefined') {
      const existingScript = document.querySelector(
        `script[src*="googletagmanager.com/gtag/js?id=${googleAnalyticsId}"]`
      );
      const hasGtag = typeof window.gtag !== 'undefined' && Array.isArray(window.dataLayer);

      if (!existingScript && !hasGtag) {
        // Load Google Analytics script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag;

        gtag('js', new Date());
        gtag('config', googleAnalyticsId, {
          page_title: 'Residencia Panama - Consulenza Immigrazione',
          page_location: window.location.href,
          custom_map: {
            custom_parameter_1: 'lead_source',
            custom_parameter_2: 'service_type'
          }
        });

        console.log('🔍 Google Analytics 4 initialized:', googleAnalyticsId);
      }

      // Track page view only if analytics consent is granted
      if (typeof window.gtag !== 'undefined') {
        try { initDefaultConsent(); } catch {}
        const consent = getConsent();
        if (consent.analytics) {
          window.gtag('event', 'page_view', {
            page_title: document.title,
            page_location: window.location.href,
            page_path: window.location.pathname
          });
        }
      }
    }

    // Facebook Pixel Setup
    if (facebookPixelId && typeof window !== 'undefined') {
      // Facebook Pixel Code
      (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode && s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq('init', facebookPixelId);
      window.fbq('track', 'PageView');

      // Track custom events for lead generation
      window.fbq('track', 'ViewContent', {
        content_name: 'Residencia Panama Homepage',
        content_category: 'Immigration Services',
        value: 0,
        currency: 'USD'
      });

      console.log('📘 Facebook Pixel initialized:', facebookPixelId);
    }

    // Enhanced eCommerce tracking for lead value
    const trackLeadValue = () => {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'session_start', {
          event_category: 'Engagement',
          event_label: 'Homepage Visit',
          value: 1,
          custom_parameters: {
            traffic_source: document.referrer || 'direct',
            user_agent: navigator.userAgent,
            page_load_time: Date.now()
          }
        });
      }
    };

    // Track session start after a short delay
    const timer = setTimeout(trackLeadValue, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [googleAnalyticsId, facebookPixelId]);

  return (
    <>
      {/* Facebook Pixel noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
};

// Global type declarations for analytics
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

// Utility functions for tracking specific events
export const trackEvent = (eventName: string, parameters: any = {}) => {
  // Google Analytics 4
  if (typeof window.gtag !== 'undefined') {
    const consent = getConsent();
    if (consent.analytics) {
      window.gtag('event', eventName, parameters);
    }
  }

  // Facebook Pixel
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', eventName, parameters);
  }

  console.log('📊 Event tracked:', eventName, parameters);
};

export const trackConversion = (conversionType: string, value: number = 0) => {
  const conversionData = {
    event_category: 'Conversion',
    event_label: conversionType,
    value: value,
    currency: 'USD'
  };

  trackEvent('conversion', conversionData);
  
  // Facebook Pixel specific conversion tracking
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'Lead', {
      content_name: conversionType,
      value: value,
      currency: 'USD'
    });
  }
};

export const trackFormSubmission = (formType: string, source: string) => {
  trackEvent('form_submit', {
    event_category: 'Lead Generation',
    event_label: formType,
    form_type: formType,
    lead_source: source,
    value: 10 // Assign value to form submissions
  });
};